/**
 * Formats database data types into user-friendly, non-technical descriptions
 */

/**
 * Formats an unknown data type in a user-friendly way
 */
const formatUnknownType = (dataType: string, maximumLength: number): string => {
  // Clean up the type name (remove underscores, capitalize first letter)
  const cleanedType = dataType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  if (maximumLength > 0 && maximumLength !== -1) {
    return `${cleanedType} (max ${maximumLength})`;
  }

  return cleanedType;
};

/**
 * Formats character/text data types
 */
const formatCharacterType = (
  type: string,
  maximumLength: number,
): string | null => {
  if (type === "varchar" || type === "nvarchar") {
    if (maximumLength === -1) {
      return "Text (unlimited length)";
    }
    if (maximumLength > 0) {
      return `Text (up to ${maximumLength} characters)`;
    }
    return "Text";
  }

  if (type === "char" || type === "nchar") {
    if (maximumLength > 0) {
      return `Text (exactly ${maximumLength} character${maximumLength === 1 ? "" : "s"})`;
    }
    return "Text (fixed length)";
  }

  if (type === "text" || type === "ntext") {
    return "Long text";
  }

  return null;
};

/**
 * Formats numeric data types
 */
const formatNumericType = (type: string): string | null => {
  if (type === "int" || type === "integer") return "Whole number";
  if (type === "bigint") return "Large whole number";
  if (type === "smallint") return "Small whole number";
  if (type === "tinyint") return "Tiny whole number";
  if (type === "decimal" || type === "numeric") return "Decimal number";
  if (type === "float" || type === "real")
    return "Decimal number (approximate)";
  if (type === "money" || type === "smallmoney") return "Currency amount";

  return null;
};

/**
 * Formats date/time data types
 */
const formatDateTimeType = (type: string): string | null => {
  if (type === "date") return "Date (without time)";
  if (type === "datetime" || type === "datetime2") return "Date and time";
  if (type === "time") return "Time (without date)";
  if (type === "datetimeoffset") return "Date and time (with timezone)";
  if (type === "smalldatetime") return "Date and time (minute precision)";
  if (type === "timestamp" || type === "rowversion")
    return "Auto-generated version number";

  return null;
};

/**
 * Formats binary data types
 */
const formatBinaryType = (
  type: string,
  maximumLength: number,
): string | null => {
  if (type === "binary" || type === "varbinary") {
    if (maximumLength === -1) {
      return "Binary data (unlimited)";
    }
    if (maximumLength > 0) {
      return `Binary data (up to ${maximumLength} bytes)`;
    }
    return "Binary data";
  }

  if (type === "image") {
    return "Image or large binary data";
  }

  return null;
};

/**
 * Formats special data types
 */
const formatSpecialType = (type: string): string | null => {
  if (type === "bit") return "Yes/No value";
  if (type === "uniqueidentifier" || type === "guid")
    return "Unique identifier (GUID)";
  if (type === "xml") return "XML document";
  if (type === "json") return "JSON document";
  if (type === "geography") return "Geographic location";
  if (type === "geometry") return "Geometric shape";
  if (type === "sql_variant") return "Variable data type";
  if (type === "hierarchyid") return "Hierarchical structure";

  return null;
};

/**
 * Converts a database data type and maximum length into a user-friendly description
 * @param dataType - The database data type (e.g., "varchar", "int", "datetime")
 * @param maximumLength - The maximum length constraint (0 means no limit or not applicable)
 * @returns A user-friendly description of the data type
 */
export const formatDataType = (dataType: string, maximumLength = 0): string => {
  const type = dataType.toLowerCase();

  // Try each category of data type
  const characterType = formatCharacterType(type, maximumLength);
  if (characterType) return characterType;

  const numericType = formatNumericType(type);
  if (numericType) return numericType;

  const dateTimeType = formatDateTimeType(type);
  if (dateTimeType) return dateTimeType;

  const binaryType = formatBinaryType(type, maximumLength);
  if (binaryType) return binaryType;

  const specialType = formatSpecialType(type);
  if (specialType) return specialType;

  // If unknown type, return a cleaned version
  return formatUnknownType(dataType, maximumLength);
};

/**
 * Gets a short description of the data type category for display
 * @param dataType - The database data type
 * @returns A short category description
 */
export const getDataTypeCategory = (dataType: string): string => {
  const type = dataType.toLowerCase();

  if (
    type.includes("char") ||
    type.includes("text") ||
    type.includes("varchar") ||
    type.includes("nvarchar")
  ) {
    return "Text";
  }

  if (
    type.includes("int") ||
    type.includes("decimal") ||
    type.includes("numeric") ||
    type.includes("float") ||
    type.includes("real") ||
    type.includes("money")
  ) {
    return "Number";
  }

  if (
    type.includes("date") ||
    type.includes("time") ||
    type.includes("timestamp")
  ) {
    return "Date/Time";
  }

  if (type === "bit") {
    return "Boolean";
  }

  if (
    type.includes("binary") ||
    type.includes("image") ||
    type.includes("varbinary")
  ) {
    return "Binary";
  }

  if (type === "uniqueidentifier" || type === "guid") {
    return "Identifier";
  }

  return "Other";
};
